import React from "react";
import _ from "lodash";
import moment from "moment";
import cx from "classnames";
import submit from "submit-form";
import { useTranslation } from "react-i18next";
import getDayRangeString from "../../../../utils/day-range-string";
import Chart from "../../../../parts/chart";
import DateInput from "../../../../parts/inputs/date-input";
import ToggleLinksInput from "../../../../parts/inputs/toggle-links-input";
import ShareModal from "../../../../parts/modals/share";
import MetricsFoot from "../../../athlete-portal/metrics/MetricsFoot";
import { getChartLabels } from "../../../../utils/chart-labels";
import { useRillContext } from "../../../../rill/RillReact/share/base";

const {
  CUSTOM_METRIC_DATA_SERIES,
  PERIOD_FORMATS,
  DATE_FORMATS: { DATE_PICKER_FORMAT },
} = require("../../../../constants");

function View(props) {
  const { locals } = useRillContext();
  const { t } = useTranslation("dashboard");

  const { query, editCustomMetric, viewCustomMetric } = locals;
  const selectedUnits = query.units;
  const selectedPeriod = query.period;
  const selectedStartDate = moment.utc(query.start, DATE_PICKER_FORMAT);
  const activeID = _.get(editCustomMetric, "_id");
  const activeName = _.get(editCustomMetric, "name", "N/A");
  const chartKeys = _.get(viewCustomMetric, "keys");
  const chartLabels = getChartLabels(chartKeys, query, selectedPeriod);
  const chartWidth = `${_.size(chartKeys) * 40}px`;
  const barRelation = _.get(viewCustomMetric, "barRelation");
  const barChartGroup = _.find(CUSTOM_METRIC_DATA_SERIES, {
    value: barRelation,
  });
  const lineRelation = _.get(viewCustomMetric, "lineRelation");
  const lineChartGroup = _.find(CUSTOM_METRIC_DATA_SERIES, {
    value: lineRelation,
  });
  const barChartAxisLabel = _.get(barChartGroup, "title");
  const lineChartAxisLabel = _.get(lineChartGroup, "title");
  const barChartSeries = buildCharts(
    _.get(viewCustomMetric, "bars"),
    chartKeys,
    barChartGroup,
    locals
  );
  const lineChartSeries = buildCharts(
    _.get(viewCustomMetric, "lines"),
    chartKeys,
    lineChartGroup,
    locals
  );
  const hasBarChartData = !_(barChartSeries).flatMap("data").isEmpty();
  const hasLineChartData = !_(lineChartSeries).flatMap("data").isEmpty();
  const barChartReducer = _.endsWith(
    _.get(editCustomMetric, "data.bar.series"),
    "-total"
  )
    ? _.sum
    : _.max;
  const lineChartReducer = _.endsWith(
    _.get(editCustomMetric, "data.line.series"),
    "-total"
  )
    ? _.sum
    : _.max;

  let maxLineChartValue = _.max(
    _.zipWith.apply(
      _,
      _.map(lineChartSeries, "data").concat(argsToArray(lineChartReducer))
    )
  );
  let maxBarChartValue = _.max(
    _.zipWith.apply(
      _,
      _.map(barChartSeries, "data").concat(argsToArray(barChartReducer))
    )
  );

  if (_.get(barChartGroup, "value") === _.get(lineChartGroup, "value")) {
    maxLineChartValue = maxBarChartValue = Math.max(
      maxLineChartValue,
      maxBarChartValue
    );
  }

  return (
    <form
      className="Main Custom-Reports Metrics"
      id="metrics-form"
      action="#_"
      method="GET"
      data-noreset
    >
      <div className="CMR-Head">
        <div
          onClick={(_) => {
            history.back();
          }}
          className="metrics-back"
        >
          {t("dashboard:competitions.metrics")}
        </div>
        <h1 className="custom-report alt">
          {t("dashboard:customMetric.title", { name: activeName })}
        </h1>
        <span className="description">
          {t("dashboard:customMetricsView.description")}
        </span>
        <a
          className="add-new edit"
          href={`/dashboard/metrics/custom/edit/${activeID}`}
        >
          {t("dashboard:customMetricsView.editReport")}
        </a>
      </div>

      <div className="CMR-Metrics">
        <div className="Date-Range">
          <div className="left-side">
            <span className="label">{t("dashboard:metrics.reportingPeriod")}</span>
            <DateInput
              name="start"
              placeholder={t("dashboard:metrics.startDate")}
              value={query.start}
              max={query.end}
              onChange={handleDayChange}
            />
            <i>{t("dashboard:metrics.to")}</i>
            <DateInput
              name="end"
              placeholder={t("dashboard:metrics.endDate")}
              value={query.end}
              min={query.start}
              onChange={handleDayChange}
            />
            <input type="submit" style={{ opacity: 0, position: "absolute" }} />
          </div>
        </div>

        <div className="Chart-Frame bar-chart">
          <div className="head">
            <div className="right-side">
              <div className="group">
                <ToggleLinksInput
                  name="period"
                  options={[
                    { text: t("dashboard:metrics.month"), value: "month" },
                    { text: t("dashboard:metrics.week"), value: "week" },
                    { text: t("dashboard:metrics.day"), value: "day" },
                  ]}
                  className="border-right"
                />
              </div>

              {Boolean(
                barRelation === "volume" || lineRelation === "volume"
              ) && (
                <div className="group">
                  <ToggleLinksInput
                    name="units"
                    options={[
                      { text: t("dashboard:customMetric.kg"), value: "metric" },
                      { text: t("dashboard:customMetric.lbs"), value: "imperial" },
                    ]}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="body">
            {(hasBarChartData || hasLineChartData) && (
              <div>
                <div className="legend">
                  {_.map(barChartSeries, ({ className, title }) => {
                    return (
                      <div className={`bar series ${className}`}>{title}</div>
                    );
                  })}

                  {_.map(lineChartSeries, ({ className, title }) => {
                    return (
                      <div className={`line series ${className}`}>{title}</div>
                    );
                  })}
                </div>
                <div className="axis-labels">
                  <div className="left">{barChartAxisLabel}</div>
                  <div className="right">{lineChartAxisLabel}</div>
                </div>
                <div className="chart-fullsize">
                  <div className="labels">
                    <Chart
                      type="Bar"
                      only="label"
                      data={{ series: barChartSeries }}
                      options={{
                        width: "60px",
                        height: "380px",
                        stackBars: true,
                        axisX: { showGrid: false, showLabel: false },
                        axisY: {
                          showGrid: false,
                          showLabel: true,
                          onlyInteger: true,
                          scaleMinSpace: 30,
                          labelInterpolationFnc: _.get(
                            barChartGroup,
                            "labelMap",
                            _.noop
                          ),
                        },
                        low: 0,
                        high: maxBarChartValue,
                      }}
                    />

                    <Chart
                      type="Line"
                      only="label"
                      data={{ series: lineChartSeries }}
                      options={{
                        width: "60px",
                        height: "380px",
                        axisX: { showGrid: false, showLabel: false },
                        axisY: {
                          stretch: true,
                          showGrid: false,
                          showLabel: true,
                          scaleMinSpace: 30,
                          labelInterpolationFnc: _.get(
                            lineChartGroup,
                            "labelMap",
                            _.noop
                          ),
                        },
                        low: 0,
                        high: maxLineChartValue,
                      }}
                    />
                  </div>

                  <div className="data">
                    <Chart
                      type="Bar"
                      data={{
                        labels: chartLabels,
                        series: barChartSeries,
                      }}
                      options={{
                        width: chartWidth,
                        height: "380px",
                        stackBars: true,
                        chartPadding: { left: 35, right: 75 },
                        axisX: { showGrid: false, showLabel: true },
                        axisY: {
                          scaleMinSpace: 30,
                          showGrid: true,
                          showLabel: false,
                        },
                        low: 0,
                        high: maxBarChartValue,
                      }}
                      tooltip={(item, i) => {
                        const key = Number(chartKeys[i]);
                        const date = selectedStartDate
                          .clone()
                          .add(key, selectedPeriod);
                        const dayRange = getDayRangeString(
                          [
                            date,
                            date
                              .clone()
                              .add(1, selectedPeriod)
                              .subtract(1, "day"),
                          ],
                          query
                        );
                        const barTotal =
                          _.size(barChartSeries) >= 2 &&
                          _.sumBy(barChartSeries, `data.${i}`);
                        return (
                          <div className="tooltip" style={{ left: item.x }}>
                            <div className="details">
                              <div>
                                <b>{dayRange}</b>
                              </div>
                              {_.map(barChartSeries, ({ title, data }) => {
                                if (!data[i]) return;
                                return (
                                  <div key={title}>
                                    {title}{" "}
                                    <b>
                                      {barChartGroup.tooltipMap(
                                        data[i],
                                        selectedUnits,
                                        locals
                                      )}{" "}
                                      {barTotal &&
                                        ` (${_.round(
                                          (data[i] / barTotal) * 100,
                                          1
                                        )}%)`}
                                    </b>
                                  </div>
                                );
                              })}
                              {barTotal && (
                                <div>
                                  {t("dashboard:customMetric.total")} {barChartGroup.title}{" "}
                                  <b>
                                    {barChartGroup.tooltipMap(
                                      barTotal,
                                      selectedUnits,
                                      locals
                                    )}
                                  </b>
                                </div>
                              )}
                              {_.map(lineChartSeries, ({ title, data }) => {
                                if (!data[i]) return;
                                return (
                                  <div key={title}>
                                    {title}{" "}
                                    <b>
                                      {lineChartGroup.tooltipMap(
                                        data[i],
                                        selectedUnits,
                                        locals
                                      )}
                                    </b>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      }}
                    />

                    <Chart
                      type="Line"
                      data={{ series: lineChartSeries }}
                      options={{
                        width: chartWidth,
                        height: "380px",
                        showPoint: false,
                        chartPadding: { left: 35, right: 75 },
                        axisX: { showGrid: false, showLabel: false },
                        axisY: {
                          scaleMinSpace: 30,
                          showGrid: false,
                          showLabel: false,
                        },
                        low: 0,
                        high: maxLineChartValue,
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <MetricsFoot />
      </div>
    </form>
  );
}

// Creates a useable chart from the server chart data.
function buildCharts(charts, keys, related, locals) {
  const result = _.map(charts, ({ group, by, series, data }) => {
    if (group === "undefined") group = "unassigned";
    return {
      title: group
        ? isFinite(group)
          ? Number(group)
          : _.startCase(group)
        : _.get(_.find(related.series, { value: series }), "title"),
      name: group || series,
      className: cx(series, by, group === "unassigned" ? "undefined" : group),
      data: _.map(keys, (key) =>
        related.dataMap(_.get(data, key), locals.query.units, locals)
      ),
    };
  });

  // Sort numerical groupings if needed.
  return _.isNumber(_.get(_.first(result), "title"))
    ? _.reverse(_.sortBy(result, "title"))
    : result;
}

function argsToArray(fn) {
  return function () {
    return fn(_.toArray(arguments));
  };
}

function handleDayChange() {
  submit("#_", { body: document.getElementById("metrics-form") });
}

const CustomView = (app) => {
  app.get(
    "/view/:customMetric?",
    async ({ req, res, locals, load, assert, api }) => {
      const { query, params } = req;
      const editCustomMetric = await load("editCustomMetric", {
        customMetric: params.customMetric,
      });
      const activeID = _.get(editCustomMetric, "_id");
      assert(
        activeID,
        404,
        "Custom metric not found, it may have been deleted."
      );

      _.defaults(query, {
        period: "day",
        units: locals.units,
        start: moment.utc().startOf("month").format(DATE_PICKER_FORMAT),
        end: moment.utc().endOf("month").format(DATE_PICKER_FORMAT),
      });

      await load("viewCustomMetric", {
        customMetric: activeID,
        start: moment
          .utc(query.start, DATE_PICKER_FORMAT)
          .startOf("day")
          .toDate(),
        end: moment.utc(query.end, DATE_PICKER_FORMAT).endOf("day").toDate(),
        period: query.period,
      });

      const modal = _.get(query, "modal", {});

      if (modal.type === "share" && !modal.snapshot) {
        // create share link
        try {
          modal.snapshot = (
            await (
              await api("snapshot", {
                method: "POST",
                body: {
                  type: "custom",
                  data: JSON.stringify(
                    _.chain(locals)
                      .pick(["viewCustomMetric", "editCustomMetric"])
                      .assign({
                        period: query.period,
                        start: query.start,
                        end: query.end,
                      })
                      .value()
                  ),
                },
              })
            ).json()
          )._id;

          modal.date = getDayRangeString([
            moment.utc(query.start, DATE_PICKER_FORMAT),
            moment.utc(query.end, DATE_PICKER_FORMAT),
          ]);
          return res.refreshWith({ modal });
        } catch (e) {
          console.error(e.stack || e);
          fail(400, "Error creating share link.");
        }
      }

      locals.modal = {
        share: <ShareModal />,
      }[modal.type];

      locals.title = `View ${editCustomMetric.name}`;
      res.body = <View />;
    }
  );
};

export default CustomView;
